import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot, Settings, MessageSquare, Send, Clock, Shield, 
  ToggleLeft, ToggleRight, ChevronRight, History, 
  CheckCircle, XCircle, AlertTriangle, Edit2, Eye,
  Facebook, Instagram, Music2, Image, RefreshCw,
  Sparkles, MessageCircle, ThumbsUp, ThumbsDown,
  Filter, Search, ArrowLeft, Zap, Brain, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600', recommendedTone: 'helpful' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-purple-500 to-pink-500', recommendedTone: 'friendly' },
  { id: 'tiktok', name: 'TikTok', icon: Music2, color: 'bg-black', recommendedTone: 'witty' },
  { id: 'pinterest', name: 'Pinterest', icon: Image, color: 'bg-red-600', recommendedTone: 'professional' }
];

const TONES = [
  { id: 'friendly', name: 'Friendly', desc: 'Warm and approachable', emoji: '😊' },
  { id: 'professional', name: 'Professional', desc: 'Polished and clear', emoji: '💼' },
  { id: 'enthusiastic', name: 'Enthusiastic', desc: 'Energetic and excited', emoji: '🎉' },
  { id: 'helpful', name: 'Helpful', desc: 'Solution-oriented', emoji: '🤝' },
  { id: 'witty', name: 'Witty', desc: 'Clever with light humor', emoji: '😏' }
];

const PLATFORM_TONE_REASONS = {
  facebook: "Facebook audiences are diverse and community-focused. Helpful tone builds trust.",
  instagram: "Instagram is personal and visual. Friendly tone feels authentic and relatable.",
  tiktok: "TikTok audiences love personality. Witty responses get more engagement.",
  pinterest: "Pinterest users seek inspiration and ideas. Professional tone adds credibility."
};

export default function AutoReplyPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  
  // Settings state
  const [settings, setSettings] = useState({
    enabled: false,
    platforms: {
      facebook: { comments: false, dms: false },
      instagram: { comments: false, dms: false },
      tiktok: { comments: false, dms: false },
      pinterest: { comments: false, dms: false }
    },
    response_tone: 'friendly',
    response_delay_seconds: 30,
    blacklist_words: [],
    review_negative: true,
    review_complaints: true,
    review_all: false,
    custom_instructions: '',
    daily_limit: 100
  });
  
  // History state
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState({ platform: null, status: null });
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Pending reviews state
  const [pendingReviews, setPendingReviews] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Test mode state
  const [testMode, setTestMode] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testPlatform, setTestPlatform] = useState('instagram');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  
  // Simulation Lab state
  const [simulationResults, setSimulationResults] = useState([]);
  const [runningSimulation, setRunningSimulation] = useState(false);
  const [currentSimIndex, setCurrentSimIndex] = useState(-1);
  
  // New blacklist word input
  const [newBlacklistWord, setNewBlacklistWord] = useState('');
  
  // Preset simulation messages - mix of challenging scenarios
  const SIMULATION_MESSAGES = [
    { id: 1, message: "Your competitor XYZ has way better features and it's cheaper too. Why should I use Viraly?", category: "Competition", platform: "facebook", expectedIntent: "question" },
    { id: 2, message: "This is absolutely TERRIBLE! I paid for premium and nothing works. I want my money back NOW!", category: "Angry Complaint", platform: "instagram", expectedIntent: "complaint" },
    { id: 3, message: "omg I LOVE this app so much!! 😍 Best decision ever to switch to you guys", category: "Compliment", platform: "tiktok", expectedIntent: "compliment" },
    { id: 4, message: "How much does the enterprise plan cost? Do you offer discounts for annual billing?", category: "Pricing Inquiry", platform: "facebook", expectedIntent: "purchase_intent" },
    { id: 5, message: "Been waiting 3 days for support to respond. This is ridiculous for a paid service.", category: "Support Complaint", platform: "instagram", expectedIntent: "complaint" },
    { id: 6, message: "Can you post to LinkedIn too or just the platforms listed? Need it for B2B", category: "Feature Question", platform: "facebook", expectedIntent: "question" },
    { id: 7, message: "lol this AI stuff is so cringe 😂 bet a 5 year old could write better captions", category: "Nasty/Troll", platform: "tiktok", expectedIntent: "complaint" },
    { id: 8, message: "Hey! First time here. What's the best way to get started with scheduling posts?", category: "Onboarding", platform: "instagram", expectedIntent: "greeting" },
    { id: 9, message: "The video generation keeps failing. Error every single time. Fix your broken product!", category: "Technical Issue", platform: "facebook", expectedIntent: "complaint" },
    { id: 10, message: "Wow the new carousel feature is 🔥🔥🔥 - used it for my client and they loved it!", category: "Positive Feedback", platform: "instagram", expectedIntent: "compliment" }
  ];

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('viraly_token') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auto-reply/settings`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (historyFilter.platform) params.append('platform', historyFilter.platform);
      if (historyFilter.status) params.append('status', historyFilter.status);
      params.append('limit', '50');
      
      const response = await fetch(`${API_URL}/api/auto-reply/history?${params}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [getAuthHeaders, historyFilter]);

  // Fetch pending reviews
  const fetchPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auto-reply/pending`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPendingReviews(data.pending || []);
      }
    } catch (error) {
      console.error('Error fetching pending:', error);
    } finally {
      setPendingLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auto-reply/stats?days=30`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, [fetchSettings, fetchStats]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    } else if (activeTab === 'review') {
      fetchPending();
    }
  }, [activeTab, fetchHistory, fetchPending]);

  // Save settings
  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/auto-reply/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        toast.success('Settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Toggle main enabled
  const toggleEnabled = async () => {
    const newEnabled = !settings.enabled;
    setSettings(prev => ({ ...prev, enabled: newEnabled }));
    await saveSettings({ enabled: newEnabled });
  };

  // Toggle platform setting
  const togglePlatform = async (platformId, type) => {
    const newPlatforms = { ...settings.platforms };
    newPlatforms[platformId] = {
      ...newPlatforms[platformId],
      [type]: !newPlatforms[platformId]?.[type]
    };
    setSettings(prev => ({ ...prev, platforms: newPlatforms }));
    await saveSettings({ platforms: newPlatforms });
  };

  // Set tone
  const setTone = async (tone) => {
    setSettings(prev => ({ ...prev, response_tone: tone }));
    await saveSettings({ response_tone: tone });
  };

  // Add blacklist word
  const addBlacklistWord = async () => {
    if (!newBlacklistWord.trim()) return;
    const updated = [...(settings.blacklist_words || []), newBlacklistWord.trim().toLowerCase()];
    setSettings(prev => ({ ...prev, blacklist_words: updated }));
    await saveSettings({ blacklist_words: updated });
    setNewBlacklistWord('');
  };

  // Remove blacklist word
  const removeBlacklistWord = async (word) => {
    const updated = settings.blacklist_words.filter(w => w !== word);
    setSettings(prev => ({ ...prev, blacklist_words: updated }));
    await saveSettings({ blacklist_words: updated });
  };

  // Test auto-reply
  const runTest = async () => {
    if (!testMessage.trim()) {
      toast.error('Enter a test message');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/auto-reply/test`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          message: testMessage,
          platform: testPlatform,
          message_type: 'comment'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTestResult(data);
      } else {
        toast.error('Test failed');
      }
    } catch (error) {
      console.error('Test error:', error);
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  // Run full simulation with all preset messages
  const runSimulation = async () => {
    setRunningSimulation(true);
    setSimulationResults([]);
    
    for (let i = 0; i < SIMULATION_MESSAGES.length; i++) {
      setCurrentSimIndex(i);
      const sim = SIMULATION_MESSAGES[i];
      
      try {
        const response = await fetch(`${API_URL}/api/auto-reply/test`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            message: sim.message,
            platform: sim.platform,
            message_type: 'comment'
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setSimulationResults(prev => [...prev, {
            ...sim,
            response: data.generated_response,
            sentiment: data.sentiment,
            intent: data.intent,
            confidence: data.confidence,
            would_need_review: data.would_need_review
          }]);
        } else {
          setSimulationResults(prev => [...prev, {
            ...sim,
            response: 'Error generating response',
            error: true
          }]);
        }
      } catch (error) {
        console.error('Simulation error:', error);
        setSimulationResults(prev => [...prev, {
          ...sim,
          response: 'Error: ' + error.message,
          error: true
        }]);
      }
      
      // Small delay between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setCurrentSimIndex(-1);
    setRunningSimulation(false);
    toast.success('Simulation complete! Review the responses below.');
  };

  // Approve response
  const approveResponse = async (historyId, editedResponse = null) => {
    try {
      const response = await fetch(`${API_URL}/api/auto-reply/approve/${historyId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ edited_response: editedResponse })
      });
      
      if (response.ok) {
        toast.success('Response approved');
        fetchPending();
      } else {
        toast.error('Failed to approve');
      }
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve');
    }
  };

  // Reject response
  const rejectResponse = async (historyId) => {
    try {
      const response = await fetch(`${API_URL}/api/auto-reply/reject/${historyId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Response rejected');
        fetchPending();
      } else {
        toast.error('Failed to reject');
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D1A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D1A] text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <Bot className="w-8 h-8 text-orange-500" />
                VIRA Auto-Reply
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                AI-powered responses for comments and DMs across all platforms
              </p>
            </div>
          </div>
          
          {/* Master Toggle */}
          <div className="flex items-center gap-3 bg-[#1A1A2E] rounded-xl px-4 py-3">
            <span className="text-sm text-gray-400">Auto-Reply</span>
            <button onClick={toggleEnabled} className="focus:outline-none">
              {settings.enabled ? (
                <ToggleRight className="w-10 h-6 text-green-500" />
              ) : (
                <ToggleLeft className="w-10 h-6 text-gray-500" />
              )}
            </button>
            <span className={`text-sm font-medium ${settings.enabled ? 'text-green-500' : 'text-gray-500'}`}>
              {settings.enabled ? 'Active' : 'Off'}
            </span>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs">Total Processed</p>
                    <p className="text-2xl font-bold">{stats.total_processed}</p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs">Sent</p>
                    <p className="text-2xl font-bold text-green-500">{stats.total_sent}</p>
                  </div>
                  <Send className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs">Pending Review</p>
                    <p className="text-2xl font-bold text-yellow-500">{stats.total_pending}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-yellow-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs">Avg Confidence</p>
                    <p className="text-2xl font-bold">{((stats.avg_confidence || 0) * 100).toFixed(0)}%</p>
                  </div>
                  <Brain className="w-8 h-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#1A1A2E] border border-gray-800">
            <TabsTrigger value="settings" className="data-[state=active]:bg-orange-500">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="test" className="data-[state=active]:bg-orange-500">
              <Sparkles className="w-4 h-4 mr-2" />
              Test
            </TabsTrigger>
            <TabsTrigger value="review" className="data-[state=active]:bg-orange-500">
              <Eye className="w-4 h-4 mr-2" />
              Review Queue
              {pendingReviews.length > 0 && (
                <Badge className="ml-2 bg-yellow-500">{pendingReviews.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-orange-500">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Platform Settings with Per-Platform Tone */}
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-500" />
                  Platform Settings
                </CardTitle>
                <CardDescription>Choose where VIRA should auto-reply and the tone for each platform</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {PLATFORMS.map(platform => {
                  const Icon = platform.icon;
                  const platformSettings = settings.platforms?.[platform.id] || {};
                  const currentTone = platformSettings.tone || settings.platform_tones?.[platform.id] || platform.recommendedTone;
                  const currentToneData = TONES.find(t => t.id === currentTone);
                  
                  return (
                    <div key={platform.id} className="p-4 bg-[#0D0D1A] rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${platform.color} rounded-lg flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <span className="font-medium">{platform.name}</span>
                            <p className="text-xs text-gray-500">{PLATFORM_TONE_REASONS[platform.id]}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Switch
                              checked={platformSettings.comments || false}
                              onCheckedChange={() => togglePlatform(platform.id, 'comments')}
                            />
                            <span className="text-sm text-gray-400">Comments</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Switch
                              checked={platformSettings.dms || false}
                              onCheckedChange={() => togglePlatform(platform.id, 'dms')}
                            />
                            <span className="text-sm text-gray-400">DMs</span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Per-platform tone selector */}
                      <div className="flex items-center gap-2 pt-2 border-t border-gray-800">
                        <span className="text-xs text-gray-500 min-w-[60px]">Tone:</span>
                        <div className="flex gap-1 flex-wrap">
                          {TONES.map(tone => (
                            <button
                              key={tone.id}
                              onClick={async () => {
                                const newPlatforms = { ...settings.platforms };
                                newPlatforms[platform.id] = {
                                  ...newPlatforms[platform.id],
                                  tone: tone.id
                                };
                                setSettings(prev => ({ ...prev, platforms: newPlatforms }));
                                await saveSettings({ platforms: newPlatforms });
                              }}
                              className={`px-2 py-1 rounded text-xs flex items-center gap-1 transition-all ${
                                currentTone === tone.id
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                              }`}
                            >
                              <span>{tone.emoji}</span>
                              <span>{tone.name}</span>
                              {tone.id === platform.recommendedTone && currentTone !== tone.id && (
                                <Badge className="ml-1 bg-green-500/20 text-green-400 text-[10px] px-1 py-0">
                                  Recommended
                                </Badge>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Global Response Tone (Fallback) */}
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-orange-500" />
                  Global Fallback Tone
                </CardTitle>
                <CardDescription>Default tone when no platform-specific tone is set</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-3">
                  {TONES.map(tone => (
                    <button
                      key={tone.id}
                      onClick={() => setTone(tone.id)}
                      className={`p-4 rounded-lg border-2 transition-all text-center ${
                        settings.response_tone === tone.id
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <span className="text-2xl">{tone.emoji}</span>
                      <p className="font-medium mt-2">{tone.name}</p>
                      <p className="text-xs text-gray-400 mt-1">{tone.desc}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Safety Settings */}
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-orange-500" />
                  Safety Controls
                </CardTitle>
                <CardDescription>Configure when human review is required</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-[#0D0D1A] rounded-lg">
                  <div>
                    <p className="font-medium">Review Negative Messages</p>
                    <p className="text-sm text-gray-400">Require approval for negative sentiment</p>
                  </div>
                  <Switch
                    checked={settings.review_negative}
                    onCheckedChange={(checked) => {
                      setSettings(prev => ({ ...prev, review_negative: checked }));
                      saveSettings({ review_negative: checked });
                    }}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-[#0D0D1A] rounded-lg">
                  <div>
                    <p className="font-medium">Review Complaints</p>
                    <p className="text-sm text-gray-400">Require approval for complaints</p>
                  </div>
                  <Switch
                    checked={settings.review_complaints}
                    onCheckedChange={(checked) => {
                      setSettings(prev => ({ ...prev, review_complaints: checked }));
                      saveSettings({ review_complaints: checked });
                    }}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-[#0D0D1A] rounded-lg">
                  <div>
                    <p className="font-medium">Review All Responses</p>
                    <p className="text-sm text-gray-400">Manually approve every response</p>
                  </div>
                  <Switch
                    checked={settings.review_all}
                    onCheckedChange={(checked) => {
                      setSettings(prev => ({ ...prev, review_all: checked }));
                      saveSettings({ review_all: checked });
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Blacklist Words */}
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-orange-500" />
                  Blacklist Words
                </CardTitle>
                <CardDescription>Messages containing these words will be skipped</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Add word to blacklist..."
                    value={newBlacklistWord}
                    onChange={(e) => setNewBlacklistWord(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addBlacklistWord()}
                    className="bg-[#0D0D1A] border-gray-700"
                  />
                  <Button onClick={addBlacklistWord} className="bg-orange-500 hover:bg-orange-600">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settings.blacklist_words?.map((word, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="bg-red-500/20 text-red-400 cursor-pointer hover:bg-red-500/30"
                      onClick={() => removeBlacklistWord(word)}
                    >
                      {word} ×
                    </Badge>
                  ))}
                  {(!settings.blacklist_words || settings.blacklist_words.length === 0) && (
                    <p className="text-gray-500 text-sm">No blacklisted words</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Custom Instructions */}
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-500" />
                  Custom Instructions
                </CardTitle>
                <CardDescription>Additional brand-specific guidelines for VIRA</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="e.g., Always mention our 24/7 support. Never discuss competitor products. Use our brand hashtag #ViralWithViraly in replies..."
                  value={settings.custom_instructions || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, custom_instructions: e.target.value }))}
                  onBlur={() => saveSettings({ custom_instructions: settings.custom_instructions })}
                  className="bg-[#0D0D1A] border-gray-700 min-h-[100px]"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Tab */}
          <TabsContent value="test" className="space-y-6">
            <Card className="bg-[#1A1A2E] border-gray-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-orange-500" />
                  Test Auto-Reply
                </CardTitle>
                <CardDescription>See how VIRA would respond to a message</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  {PLATFORMS.map(platform => {
                    const Icon = platform.icon;
                    return (
                      <button
                        key={platform.id}
                        onClick={() => setTestPlatform(platform.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                          testPlatform === platform.id
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-gray-700 hover:border-gray-600'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {platform.name}
                      </button>
                    );
                  })}
                </div>
                
                <Textarea
                  placeholder="Enter a test comment or DM message..."
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="bg-[#0D0D1A] border-gray-700 min-h-[100px]"
                />
                
                <Button
                  onClick={runTest}
                  disabled={testing || !testMessage.trim()}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {testing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Response
                    </>
                  )}
                </Button>
                
                {testResult && (
                  <div className="mt-6 space-y-4">
                    <div className="flex gap-4">
                      <Badge className={`${
                        testResult.sentiment === 'positive' ? 'bg-green-500' :
                        testResult.sentiment === 'negative' ? 'bg-red-500' : 'bg-gray-500'
                      }`}>
                        {testResult.sentiment}
                      </Badge>
                      <Badge className="bg-blue-500">
                        Intent: {testResult.intent}
                      </Badge>
                      <Badge className="bg-purple-500">
                        Confidence: {((testResult.confidence || 0) * 100).toFixed(0)}%
                      </Badge>
                      {testResult.would_need_review && (
                        <Badge className="bg-yellow-500">
                          Would need review
                        </Badge>
                      )}
                    </div>
                    
                    <div className="bg-[#0D0D1A] rounded-lg p-4">
                      <p className="text-gray-400 text-sm mb-2">Incoming Message:</p>
                      <p className="text-white mb-4">"{testResult.incoming_message}"</p>
                      
                      <p className="text-orange-400 text-sm mb-2">VIRA's Response:</p>
                      <p className="text-white text-lg">{testResult.generated_response}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Simulation Lab */}
            <Card className="bg-[#1A1A2E] border-gray-800 mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-orange-500" />
                  Simulation Lab
                </CardTitle>
                <CardDescription>
                  Run 10 pre-configured scenarios including nasty comments, pricing questions, competitor mentions, and complaints to see how VIRA handles them
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={runSimulation}
                  disabled={runningSimulation}
                  className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 mb-6"
                >
                  {runningSimulation ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Running Simulation ({currentSimIndex + 1}/{SIMULATION_MESSAGES.length})...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Run Full Simulation (10 Scenarios)
                    </>
                  )}
                </Button>

                {/* Preview of scenarios */}
                {simulationResults.length === 0 && !runningSimulation && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-400 mb-3">Scenarios include:</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {['Competition', 'Angry Complaint', 'Compliment', 'Pricing', 'Support Issue', 'Feature Question', 'Troll Comment', 'Onboarding', 'Tech Issue', 'Positive'].map((cat, i) => (
                        <Badge key={i} variant="outline" className="border-gray-600 text-gray-400 text-xs justify-center">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Simulation Results */}
                {simulationResults.length > 0 && (
                  <div className="space-y-4 mt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-white">Simulation Results</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSimulationResults([])}
                        className="text-gray-400 hover:text-white"
                      >
                        Clear Results
                      </Button>
                    </div>
                    
                    {simulationResults.map((result, index) => {
                      const platformIcon = {
                        facebook: Facebook,
                        instagram: Instagram,
                        tiktok: Music2,
                        pinterest: Image
                      };
                      const PlatformIcon = platformIcon[result.platform] || MessageSquare;
                      
                      return (
                        <div key={result.id || index} className="bg-[#0D0D1A] rounded-lg p-4 border border-gray-800">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-gray-700 text-white text-xs">#{index + 1}</Badge>
                              <Badge variant="outline" className="border-gray-600 text-gray-300 text-xs">
                                {result.category}
                              </Badge>
                              <PlatformIcon className="w-4 h-4 text-gray-400" />
                            </div>
                            <div className="flex items-center gap-2">
                              {result.sentiment && (
                                <Badge className={`text-xs ${
                                  result.sentiment === 'positive' ? 'bg-green-500/20 text-green-400' :
                                  result.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {result.sentiment}
                                </Badge>
                              )}
                              {result.confidence && (
                                <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                                  {(result.confidence * 100).toFixed(0)}%
                                </Badge>
                              )}
                              {result.would_need_review && (
                                <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                                  Review
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-1">Customer Message:</p>
                            <p className="text-gray-300 text-sm">"{result.message}"</p>
                          </div>
                          
                          <div className="bg-[#1A1A2E] rounded-lg p-3">
                            <p className="text-xs text-orange-400 mb-1">VIRA's Response:</p>
                            <p className={`text-sm ${result.error ? 'text-red-400' : 'text-white'}`}>
                              {result.response}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Review Queue Tab */}
          <TabsContent value="review" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Pending Reviews ({pendingReviews.length})
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPending}
                className="border-gray-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            {pendingLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            ) : pendingReviews.length === 0 ? (
              <Card className="bg-[#1A1A2E] border-gray-800">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-400">No responses pending review</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingReviews.map((item, index) => (
                  <PendingReviewCard
                    key={item.id || index}
                    item={item}
                    onApprove={approveResponse}
                    onReject={rejectResponse}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex gap-2">
                {['all', 'sent', 'pending', 'skipped'].map(status => (
                  <Button
                    key={status}
                    variant={historyFilter.status === (status === 'all' ? null : status) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setHistoryFilter(prev => ({
                      ...prev,
                      status: status === 'all' ? null : status
                    }))}
                    className={historyFilter.status === (status === 'all' ? null : status) ? 'bg-orange-500' : 'border-gray-700'}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchHistory}
                className="border-gray-700 ml-auto"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            {historyLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            ) : history.length === 0 ? (
              <Card className="bg-[#1A1A2E] border-gray-800">
                <CardContent className="py-12 text-center">
                  <History className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No auto-reply history yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {history.map((item, index) => (
                  <HistoryCard key={item.id || index} item={item} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Pending Review Card Component
function PendingReviewCard({ item, onApprove, onReject }) {
  const [editing, setEditing] = useState(false);
  const [editedResponse, setEditedResponse] = useState(item.response || '');
  
  const platformIcon = {
    facebook: Facebook,
    instagram: Instagram,
    tiktok: Music2,
    pinterest: Image
  };
  
  const Icon = platformIcon[item.platform] || MessageSquare;
  
  return (
    <Card className="bg-[#1A1A2E] border-gray-800">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-400">
                {item.sender_name || 'User'} • {item.message_type}
              </p>
              <p className="text-xs text-gray-500">
                {new Date(item.created_at).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge className={`${
              item.sentiment === 'positive' ? 'bg-green-500/20 text-green-400' :
              item.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'
            }`}>
              {item.sentiment}
            </Badge>
            <Badge className="bg-blue-500/20 text-blue-400">
              {item.intent}
            </Badge>
          </div>
        </div>
        
        <div className="bg-[#0D0D1A] rounded-lg p-3 mb-4">
          <p className="text-gray-400 text-xs mb-1">Their message:</p>
          <p className="text-white">"{item.incoming_message}"</p>
        </div>
        
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4">
          <p className="text-orange-400 text-xs mb-1">VIRA's suggested response:</p>
          {editing ? (
            <Textarea
              value={editedResponse}
              onChange={(e) => setEditedResponse(e.target.value)}
              className="bg-[#0D0D1A] border-gray-700 mt-2"
            />
          ) : (
            <p className="text-white">{item.response}</p>
          )}
        </div>
        
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(!editing)}
            className="border-gray-700"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            {editing ? 'Cancel Edit' : 'Edit'}
          </Button>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onReject(item.id)}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button
              size="sm"
              onClick={() => onApprove(item.id, editing ? editedResponse : null)}
              className="bg-green-600 hover:bg-green-700"
            >
              <ThumbsUp className="w-4 h-4 mr-2" />
              Approve & Send
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// History Card Component
function HistoryCard({ item }) {
  const platformIcon = {
    facebook: Facebook,
    instagram: Instagram,
    tiktok: Music2,
    pinterest: Image
  };
  
  const Icon = platformIcon[item.platform] || MessageSquare;
  
  return (
    <div className="bg-[#1A1A2E] border border-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-400">
            {item.sender_name || 'User'} • {item.message_type}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {item.sent && (
            <Badge className="bg-green-500/20 text-green-400">
              <CheckCircle className="w-3 h-3 mr-1" />
              Sent
            </Badge>
          )}
          {item.skipped && (
            <Badge className="bg-gray-500/20 text-gray-400">
              Skipped
            </Badge>
          )}
          {item.needs_review && !item.reviewed && (
            <Badge className="bg-yellow-500/20 text-yellow-400">
              Pending
            </Badge>
          )}
          <span className="text-xs text-gray-500">
            {new Date(item.created_at).toLocaleString()}
          </span>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#0D0D1A] rounded p-2">
          <p className="text-xs text-gray-500 mb-1">Message:</p>
          <p className="text-sm text-gray-300 truncate">{item.incoming_message}</p>
        </div>
        <div className="bg-[#0D0D1A] rounded p-2">
          <p className="text-xs text-gray-500 mb-1">Response:</p>
          <p className="text-sm text-gray-300 truncate">
            {item.response || item.skip_reason || 'No response'}
          </p>
        </div>
      </div>
    </div>
  );
}
